import { useState, type ReactNode } from 'react'
import {
  CheckIcon,
  ClipboardDocumentIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'

/* ------------------------------------------------------------------ *
 * Content primitives — small, self-contained helpers used only by the
 * docs sections below. They encode the admin-dashboard design system:
 *   page bg dash-bg · cards dash-surface · code #0d0d10 · body dash-secondary
 *   headings dash-text · fine print dash-muted · borders dash-border
 *   accent/links dash-accent (indigo); green = success, red = error only.
 * Body is Inter (font-sans, default); code/field names are JetBrains
 * Mono (font-mono).
 * ------------------------------------------------------------------ */

/** Inline monospace token — endpoints, field names, literal values. */
export function Code({ children }: { children: ReactNode }) {
  return (
    <code className="rounded border border-dash-border bg-[#0d0d10] px-1.5 py-0.5 font-mono text-[0.85em] text-indigo-300">
      {children}
    </code>
  )
}

/** Fenced block with a language label and a copy-to-clipboard button. */
export function CodeBlock({
  code,
  lang,
  label,
}: {
  code: string
  lang?: string
  label?: string
}) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard blocked — nothing actionable to surface */
    }
  }

  return (
    <div className="my-5 overflow-hidden rounded-lg border border-dash-border bg-[#0d0d10]">
      <div className="flex items-center justify-between border-b border-dash-border px-4 py-2">
        <span className="font-mono text-xs uppercase tracking-wide text-dash-muted">
          {label || lang || 'code'}
        </span>
        <button
          type="button"
          onClick={copy}
          aria-label={copied ? 'Copied to clipboard' : 'Copy code to clipboard'}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-dash-secondary transition-colors hover:text-dash-text focus:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d10]"
        >
          {copied ? (
            <>
              <CheckIcon className="h-4 w-4 text-green-400" aria-hidden="true" />
              Copied
            </>
          ) : (
            <>
              <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
              Copy
            </>
          )}
        </button>
      </div>
      <div className="overflow-x-auto">
        <pre className="p-4 text-sm leading-relaxed">
          <code className="whitespace-pre font-mono text-dash-text">{code}</code>
        </pre>
      </div>
    </div>
  )
}

/** Styled table with a muted header and a mobile horizontal-scroll wrapper. */
export function Table({
  head,
  rows,
}: {
  head: ReactNode[]
  rows: ReactNode[][]
}) {
  return (
    <div className="my-5 overflow-x-auto rounded-lg border border-dash-border">
      <table className="w-full min-w-[32rem] text-left text-sm">
        <thead>
          <tr className="border-b border-dash-border bg-[#0d0d10]">
            {head.map((h, i) => (
              <th
                key={i}
                scope="col"
                className="whitespace-nowrap px-4 py-2.5 font-semibold text-dash-secondary"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className="border-b border-dash-border last:border-0">
              {row.map((cell, ci) => (
                <td key={ci} className="px-4 py-2.5 align-top text-dash-secondary">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Tinted note box — info (green) or warn (red). */
export function Callout({
  kind = 'info',
  children,
}: {
  kind?: 'info' | 'warn'
  children: ReactNode
}) {
  const isWarn = kind === 'warn'
  const Icon = isWarn ? ExclamationTriangleIcon : InformationCircleIcon
  return (
    <div
      className={`my-5 flex gap-3 rounded-lg border px-4 py-3 ${
        isWarn ? 'border-red-500/30 bg-red-500/5' : 'border-dash-accent/30 bg-dash-accent/10'
      }`}
    >
      <Icon
        className={`mt-0.5 h-5 w-5 flex-none ${isWarn ? 'text-red-400' : 'text-dash-accent'}`}
        aria-hidden="true"
      />
      <div className="text-sm leading-relaxed text-dash-secondary [&_a]:text-dash-accent [&_strong]:text-dash-text">
        {children}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Prose helpers — keep the section bodies terse and consistent.
 * ------------------------------------------------------------------ */

function P({ children }: { children: ReactNode }) {
  return <p className="mb-4 leading-relaxed text-dash-secondary [&_strong]:text-dash-text">{children}</p>
}

function H3({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <h3 id={id} className="mb-3 mt-8 scroll-mt-20 text-lg font-semibold text-dash-text">
      {children}
    </h3>
  )
}

function List({ children }: { children: ReactNode }) {
  return (
    <ul className="mb-4 list-disc space-y-2 pl-5 text-dash-secondary marker:text-dash-border [&_strong]:text-dash-text">
      {children}
    </ul>
  )
}

function OrderedList({ children }: { children: ReactNode }) {
  return (
    <ol className="mb-4 list-decimal space-y-2 pl-5 text-dash-secondary marker:text-dash-muted [&_strong]:text-dash-text">
      {children}
    </ol>
  )
}

/* ------------------------------------------------------------------ *
 * Section content. Each entry is { id, title, element }; DocsPage
 * renders the <section> wrapper + <h2> and derives the sidebar from
 * this array, so the id is the anchor target (e.g. /docs#match-events).
 * ------------------------------------------------------------------ */

export type DocsSection = {
  id: string
  title: string
  element: ReactNode
}

const BASE_URL = 'https://sportsmeld.arbitragex.pro'

const matchRequest = `POST /api/match
Authorization: Bearer sportsmeld_xxx
Content-Type: application/json

{
  "reference": "Manchester United",
  "names": ["Man Utd", "Manchester City", "MUFC"],
  "sport": "soccer"
}`

const matchResponse = `{
  "reference": "Manchester United",
  "any_match": true,
  "details": [
    {
      "name": "Man Utd",
      "match": true,
      "confidence": 0.97,
      "ambiguous": false,
      "method": "alias_matcher",
      "reason_code": "abbreviation",
      "normalized_name": "man utd",
      "qualifiers": null,
      "cached": false,
      "siamese_similarity": 0.91,
      "confusable_probability": null,
      "alias_probability": 0.95,
      "decision": "match",
      "ambiguous_candidates": null
    }
  ],
  "processing_time_ms": 12.4
}`

const eventsRequest = `POST /api/match-events
Authorization: Bearer sportsmeld_xxx
Content-Type: application/json

{
  "sport": "soccer",
  "sources": [
    {
      "name": "bet365",
      "events": [
        {"id": "b1", "left": "Man Utd", "right": "Liverpool", "timestamp": 1720008000}
      ]
    },
    {
      "name": "pinnacle",
      "events": [
        {"id": "p1", "left": "Manchester United", "right": "Liverpool FC", "timestamp": 1720008000},
        {"id": "p2", "left": "Arsenal", "right": "Chelsea", "timestamp": 1720011600}
      ]
    }
  ]
}`

const eventsResponse = `{
  "sport": "soccer",
  "matched": [
    {
      "cluster_id": 0,
      "events": [
        {"source": "bet365",   "id": "b1", "left": "Man Utd",           "right": "Liverpool"},
        {"source": "pinnacle", "id": "p1", "left": "Manchester United", "right": "Liverpool FC"}
      ]
    }
  ],
  "partial": [],
  "unmatched": {
    "pinnacle": [
      {"id": "p2", "left": "Arsenal", "right": "Chelsea"}
    ]
  },
  "stats": {
    "total_events": 3,
    "total_sources": 2,
    "full_matches": 1,
    "partial_matches": 0,
    "unmatched_events": 1,
    "pairs_compared": 2,
    "pairs_processed": 2,
    "processing_time_ms": 34.7
  }
}`

const rateLimitHeaders = `X-RateLimit-Limit: 15
X-RateLimit-Remaining: 14
X-RateLimit-Reset: 1720008001`

const rateLimitBody = `{
  "detail": "Rate limit exceeded. Retry after 1 second.",
  "error": "rate_limited",
  "limit_type": "second",
  "retry_after": 0.42
}`

const authHeaders = `Authorization: Bearer sportsmeld_xxxxxxxx...
Authorization: ApiKey sportsmeld_xxxxxxxx...
X-API-Key: sportsmeld_xxxxxxxx...`

const curlMatch = `curl -s ${BASE_URL}/api/match \\
  -H "Authorization: Bearer $SPORTSMELD_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"reference":"Manchester United","names":["Man Utd","Manchester City"],"sport":"soccer"}'`

const curlEvents = `curl -s ${BASE_URL}/api/match-events \\
  -H "Authorization: Bearer $SPORTSMELD_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "sport": "soccer",
    "sources": [
      {"name":"providerA","events":[{"id":"a1","left":"Man Utd","right":"Liverpool","timestamp":1720008000}]},
      {"name":"providerB","events":[{"id":"b1","left":"Manchester United","right":"Liverpool FC","timestamp":1720008000}]}
    ]
  }'`

export const sections: DocsSection[] = [
  {
    id: 'introduction',
    title: 'Introduction',
    element: (
      <>
        <P>
          SportsMeld is an <strong>entity-resolution API for sports</strong>. Give it two team or
          player names — "Manchester United" and "Man Utd" — and it tells you whether they refer to
          the same real-world entity, while keeping look-alikes apart ("Manchester United" ≠
          "Manchester City").
        </P>
        <P>
          It also matches whole event feeds across providers: give it the fixture lists from two
          bookmakers and it tells you which rows are the same game.
        </P>
        <P>
          All endpoints live under the base URL <Code>{BASE_URL}</Code>.
        </P>
      </>
    ),
  },
  {
    id: 'getting-access',
    title: 'Getting access',
    element: (
      <>
        <OrderedList>
          <li>
            <strong>Register</strong> on the dashboard (<Code>/admin/login</Code> → <em>Register</em>).
            There is no password — you optionally provide an email, and you receive an{' '}
            <strong>API token shown once</strong>. Save it immediately; it cannot be displayed
            again. Registration is IP-limited (3/hour, 5/day).
          </li>
          <li>
            Your token starts <strong>inactive</strong> (no plan). Calling the API returns{' '}
            <Code>403</Code> — <em>"No active plan. Contact an administrator to activate your
            token."</em>
          </li>
          <li>
            An <strong>administrator activates your token</strong> by assigning a plan. There is no
            self-serve checkout.
          </li>
        </OrderedList>
        <Callout kind="info">
          Tokens look like <Code>sportsmeld_&lt;random&gt;</Code>. Only a hashed copy is stored
          server-side — treat the token like a password. If it is lost or compromised, an
          administrator can renew it (a new value is issued; the old one stops working instantly).
        </Callout>

        <H3 id="plans">Plans</H3>
        <Table
          head={[
            'Plan',
            'Requests / second',
            'Max names per /api/match',
            'Max sources per /api/match-events',
            'Max events per source',
          ]}
          rows={[
            [<strong>Bettor</strong>, '3', '5', '2', '50'],
            [<strong>Scout</strong>, '15', '25', '5', '500'],
            [
              <strong>Custom</strong>,
              'set by admin',
              'set by admin',
              'set by admin (up to 200)',
              'set by admin (up to 5,000)',
            ],
          ]}
        />
        <P>Any individual limit can also be overridden per-token by an administrator.</P>
      </>
    ),
  },
  {
    id: 'authentication',
    title: 'Authentication',
    element: (
      <>
        <P>
          Every API request must carry your token. Three equivalent header forms are accepted:
        </P>
        <CodeBlock label="HTTP headers" lang="http" code={authHeaders} />
        <List>
          <li>
            <Code>POST /api/match-events</Code> <strong>always</strong> requires a token.
          </li>
          <li>
            <Code>POST /api/match</Code> requires a token except for a small fixed set of demo
            examples used by the public landing page.
          </li>
          <li>
            Invalid or expired token → <Code>401</Code>{' '}
            <Code>{'{"detail": "Invalid API key."}'}</Code>
          </li>
          <li>
            Missing token → <Code>401</Code> with detail{' '}
            <em>"Authentication required. Provide an API token via 'Authorization: Bearer &lt;token&gt;' header."</em>
          </li>
        </List>
      </>
    ),
  },
  {
    id: 'supported-sports',
    title: 'Supported sports',
    element: (
      <>
        <P>Eight sports are supported:</P>
        <div className="my-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[
            'soccer',
            'basketball',
            'tennis',
            'american_football',
            'volleyball',
            'ice_hockey',
            'baseball',
            'rugby',
          ].map((sport) => (
            <span
              key={sport}
              className="rounded-md border border-dash-border bg-dash-surface px-3 py-2 text-center font-mono text-sm text-dash-text"
            >
              {sport}
            </span>
          ))}
        </div>
        <P>
          Pass the sport in lowercase with underscores (e.g. <Code>american_football</Code>). An
          unsupported value returns <Code>400</Code> with the valid list.
        </P>
      </>
    ),
  },
  {
    id: 'match',
    title: 'POST /api/match',
    element: (
      <>
        <P>
          Compare names against a reference. Ask: <em>"Which of these names refer to the same
          entity as my reference name?"</em>
        </P>

        <H3 id="match-request">Request</H3>
        <CodeBlock label="Request" lang="json" code={matchRequest} />
        <Table
          head={['Field', 'Type', 'Rules']}
          rows={[
            [<Code>reference</Code>, 'string', 'required; 1–500 chars'],
            [
              <Code>names</Code>,
              'string[]',
              'required; each ≤ 500 chars; count limited by your plan (Bettor 5, Scout 25); total input ≤ 10,000 chars',
            ],
            [<Code>sport</Code>, 'string', 'required; one of the supported sports'],
          ]}
        />
        <P>
          Names may contain letters (any language), digits, spaces, and{' '}
          <Code>- . ' &amp; ( ) , / :</Code>.
        </P>

        <H3 id="match-response">Response</H3>
        <CodeBlock label="Response" lang="json" code={matchResponse} />
        <P>
          <strong>How to read it:</strong>
        </P>
        <List>
          <li>
            <Code>match</Code> is the answer. <Code>true</Code> = same entity as the reference. Use
            this boolean — don't invent your own confidence threshold.
          </li>
          <li>
            <Code>confidence</Code> expresses how sure the system is <em>of its decision</em>
            (whichever way it decided).
          </li>
          <li>
            <Code>ambiguous</Code> — the name maps to more than one plausible entity (e.g.{' '}
            <Code>"FCB"</Code> → Barcelona or Bayern). When true, <Code>ambiguous_candidates</Code>{' '}
            lists them.
          </li>
          <li>
            <Code>method</Code> — which pipeline stage decided: <Code>fast_path</Code>,{' '}
            <Code>siamese_reject</Code>, <Code>confusable_reject</Code>, <Code>alias_matcher</Code>,{' '}
            <Code>qualifier_mismatch</Code>, <Code>ambiguous_acronym</Code>.
          </li>
          <li>
            <Code>reason_code</Code> — human-readable cause when available: <Code>alias</Code>,{' '}
            <Code>abbreviation</Code>, <Code>conflict_gender</Code>, <Code>ambiguous_acronym</Code>,
            …
          </li>
          <li>
            <Code>qualifiers</Code> — qualifiers extracted from the name, e.g.{' '}
            <Code>"gender=women, age=u19"</Code>. A base-name match with conflicting qualifiers is
            rejected: <em>Arsenal</em> ≠ <em>Arsenal Women</em>.
          </li>
          <li>
            <Code>cached</Code> — result served from cache (identical semantics, faster).
          </li>
          <li>
            <Code>siamese_similarity</Code> / <Code>confusable_probability</Code> /{' '}
            <Code>alias_probability</Code> are diagnostics from the internal ML stages; they may be{' '}
            <Code>null</Code> depending on which stage decided.
          </li>
        </List>
      </>
    ),
  },
  {
    id: 'match-events',
    title: 'POST /api/match-events',
    element: (
      <>
        <P>
          Match event feeds across providers. Ask: <em>"Given fixture lists from several sources,
          which rows are the same game?"</em>
        </P>

        <H3 id="events-request">Request</H3>
        <CodeBlock label="Request" lang="json" code={eventsRequest} />
        <Table
          head={['Field', 'Type', 'Rules']}
          rows={[
            [<Code>sport</Code>, 'string', 'required'],
            [
              <Code>sources</Code>,
              'array',
              <>
                <strong>at least 2</strong>, at most your plan's source limit; source{' '}
                <Code>name</Code>s must be unique
              </>,
            ],
            [<Code>sources[].name</Code>, 'string', 'your label for the provider'],
            [<Code>sources[].events[]</Code>, 'array', "at most your plan's per-source limit"],
            [
              <>
                <Code>events[].left</Code> / <Code>right</Code>
              </>,
              'string',
              'required; home/away (or player 1/2); ≤ 500 chars',
            ],
            [
              <Code>events[].id</Code>,
              'string',
              'optional — echoed back so you can map results to your own records. Recommended.',
            ],
            [
              <Code>events[].timestamp</Code>,
              'integer',
              'optional — event start as UTC epoch seconds. Recommended.',
            ],
          ]}
        />
        <Callout kind="warn">
          <strong>Timestamps matter.</strong> If <em>both</em> events in a comparison carry a
          timestamp, they only match when within <strong>12 hours</strong> of each other. This
          prevents two different fixtures between the same teams (e.g. a doubleheader or reverse
          fixture in the same week) from being merged. If either timestamp is missing, no time
          filtering is applied for that pair.
        </Callout>

        <H3 id="events-response">Response</H3>
        <CodeBlock label="Response" lang="json" code={eventsResponse} />
        <P>
          <strong>How to read it:</strong>
        </P>
        <List>
          <li>
            <Code>matched</Code> — clusters where the same game was found in <strong>every</strong>{' '}
            source. Each cluster holds one event per source, with your <Code>id</Code>s echoed back.
          </li>
          <li>
            <Code>partial</Code> — the game was found in <strong>some but not all</strong> sources
            (only possible with 3+ sources). Entries additionally include{' '}
            <Code>sources_matched</Code> and <Code>sources_missing</Code>.
          </li>
          <li>
            <Code>unmatched</Code> — events with no counterpart anywhere else, grouped by source
            name.
          </li>
          <li>
            <Code>stats</Code> — totals and timing. If your exact request was answered from cache,{' '}
            <Code>processing_time_ms</Code> reflects the (fast) cache serve and the original compute
            time appears in <Code>original_processing_time_ms</Code>.
          </li>
        </List>
        <Callout kind="info">
          Matching is order-insensitive within an event: home/away swaps between providers are still
          matched.
        </Callout>
      </>
    ),
  },
  {
    id: 'rate-limits',
    title: 'Rate limits & errors',
    element: (
      <>
        <P>Your per-second limit comes from your plan. Every response carries:</P>
        <CodeBlock label="Response headers" lang="http" code={rateLimitHeaders} />
        <P>
          Exceeding it returns <Code>429</Code> with a <Code>Retry-After</Code> header:
        </P>
        <CodeBlock label="429 body" lang="json" code={rateLimitBody} />
        <P>
          Back off for <Code>retry_after</Code> seconds and retry.
        </P>

        <H3 id="error-reference">Error reference</H3>
        <Table
          head={['Status', 'Meaning', 'Example']}
          rows={[
            [
              <Code>400</Code>,
              'Business-rule violation',
              'Unsupported sport, too many names/sources/events, duplicate source names, fewer than 2 sources',
            ],
            [
              <Code>401</Code>,
              'Missing or invalid/expired token',
              <Code>{'{"detail": "Invalid API key."}'}</Code>,
            ],
            [
              <Code>403</Code>,
              'Token valid but not permitted',
              "No active plan; or plan doesn't include event matching",
            ],
            [
              <Code>422</Code>,
              'Request shape invalid (schema validation)',
              "Name too long, empty string, wrong type — FastAPI's standard detail array",
            ],
            [<Code>429</Code>, 'Rate limit exceeded', 'See above; honor Retry-After'],
            [
              <Code>503</Code>,
              'Maintenance mode or server still starting',
              'Retry after a few seconds',
            ],
            [<Code>500</Code>, 'Internal error', 'Retry; report if persistent'],
          ]}
        />
      </>
    ),
  },
  {
    id: 'dashboard',
    title: 'The dashboard',
    element: (
      <>
        <P>
          Log in at <Code>/admin/login</Code> using your <strong>API token</strong> (there is no
          username/password). A secure session cookie is set for your browser.
        </P>
        <P>As a regular user you get three pages:</P>
        <List>
          <li>
            <strong>My Token</strong> (<Code>/admin/my-token</Code>) — your token prefix, plan,
            status, rate limit, and lifetime usage counters (total requests, match requests, event
            requests, pairs processed/compared). Read-only; renewals are done by an administrator.
          </li>
          <li>
            <strong>Requests</strong> (<Code>/admin/requests</Code>) — your own recent API calls:
            endpoint, status, duration, whether it was cached, and the full request/response bodies
            for debugging.
          </li>
          <li>
            <strong>Feedback</strong> (<Code>/admin/feedback</Code>) — report matching mistakes and
            track the status of your reports (<Code>pending</Code> → <Code>reviewed</Code>/
            <Code>dismissed</Code>).
          </li>
        </List>
        <P>
          Reviewer and admin accounts see additional pages (insights triage, token management,
          system monitoring) that regular users don't need.
        </P>
      </>
    ),
  },
  {
    id: 'reporting',
    title: 'Reporting a wrong result',
    element: (
      <>
        <P>
          If SportsMeld gets a pair wrong, report it — reports feed the review queue and future
          model retraining.
        </P>
        <P>
          On <strong>Feedback → Submit Feedback</strong> choose:
        </P>
        <List>
          <li>
            <strong>Type</strong>:
            <List>
              <li>
                <strong>False Positive (fp)</strong> — it said <em>match</em> but the entities are
                actually different.
              </li>
              <li>
                <strong>False Negative (fn)</strong> — it said <em>no match</em> but they're
                actually the same.
              </li>
            </List>
          </li>
          <li>
            <strong>Mode</strong>:
            <List>
              <li>
                <strong>Single Match</strong> — enter the two names + sport (for{' '}
                <Code>/api/match</Code> mistakes).
              </li>
              <li>
                <strong>Event Match</strong> — enter both events (left/right for each source) +
                sport (for <Code>/api/match-events</Code> mistakes).
              </li>
            </List>
          </li>
          <li>Optional notes.</li>
        </List>
        <P>
          Duplicates are detected automatically — if someone already reported the same pair you'll
          be told. You can also submit programmatically via <Code>POST /api/feedback</Code> (same
          token auth):
        </P>
        <CodeBlock
          label="POST /api/feedback body"
          lang="json"
          code={`{
  "type": "fp" | "fn",
  "match_mode": "single" | "event",
  "name1": "...",
  "name2": "...",
  "sport": "...",
  "notes": "optional",
  "event1_left": "optional",
  "event1_right": "optional",
  "event2_left": "optional",
  "event2_right": "optional"
}`}
        />
      </>
    ),
  },
  {
    id: 'best-practices',
    title: 'Best practices',
    element: (
      <OrderedList>
        <li>
          <strong>Always send <Code>id</Code>s in match-events</strong> — results echo them back, so
          you can join clusters to your own database without string comparisons.
        </li>
        <li>
          <strong>Always send <Code>timestamp</Code>s</strong> — the ±12-hour window prevents
          same-team different-game merges, which name matching alone cannot distinguish.
        </li>
        <li>
          <strong>Batch sensibly.</strong> One request with all of a provider's current fixtures
          beats many tiny requests — but very large slates (hundreds of events per source) take
          proportionally longer. If you poll frequently, keep slates lean (drop finished/expired
          events client-side).
        </li>
        <li>
          <strong>Identical repeated requests are cheap.</strong> Responses to byte-equivalent
          requests are cached; you'll see <Code>cached</Code>/<Code>original_processing_time_ms</Code>{' '}
          markers. Concurrent identical requests are deduplicated server-side — the second caller
          waits for the first computation instead of doubling the work.
        </li>
        <li>
          <strong>Handle <Code>429</Code> with backoff</strong> using the <Code>Retry-After</Code>{' '}
          header rather than hammering.
        </li>
        <li>
          <strong>Trust <Code>match</Code>, not raw scores.</strong> The internal thresholds are
          tuned per sport; the boolean is the product.
        </li>
        <li>
          <strong>Check <Code>ambiguous</Code></strong> before acting on acronyms —{' '}
          <Code>FCB</Code>-style inputs list their candidate entities instead of guessing.
        </li>
        <li>
          <strong>Report mistakes</strong> via Feedback — corrections directly improve future
          accuracy.
        </li>
      </OrderedList>
    ),
  },
  {
    id: 'quick-start',
    title: 'Quick start (curl)',
    element: (
      <>
        <H3 id="quick-start-match">Name matching</H3>
        <CodeBlock label="curl" lang="bash" code={curlMatch} />
        <H3 id="quick-start-events">Event matching</H3>
        <CodeBlock label="curl" lang="bash" code={curlEvents} />
      </>
    ),
  },
]
